// src/components/OutgrowEmbed.jsx
import React, { useEffect, useLayoutEffect, useRef } from "react";

export default function OutgrowEmbed({
  url,
  title = "Outgrow Calculator",
  fill = false,
  minHeight = 620,
  maxHeight = 980,
  bottomPad = 24,
  ring = true,
}) {
  const wrapRef = useRef(null);
  const frameRef = useRef(null);

  const resizeToViewport = () => {
    if (fill) return; // parent controls height when fill=true
    const wrap = wrapRef.current, frame = frameRef.current;
    if (!wrap || !frame) return;
    const rect = wrap.getBoundingClientRect();
    const vh =
      window.visualViewport?.height ??
      window.innerHeight ??
      document.documentElement.clientHeight;
    const available = Math.max(0, vh - rect.top - bottomPad);
    const h = Math.max(minHeight, Math.min(maxHeight, Math.round(available)));
    frame.style.height = `${h}px`;
  };

  useLayoutEffect(() => { resizeToViewport(); }, []);

  useEffect(() => {
    if (fill) return;
    const onResize = () => resizeToViewport();

    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);

    let ro;
    try {
      ro = new ResizeObserver(onResize);
      if (wrapRef.current) ro.observe(wrapRef.current);
    } catch {}

    document.fonts?.ready?.then?.(onResize);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      ro && ro.disconnect();
    };
  }, [fill]);

  return (
    <div
      ref={wrapRef}
      className={ring ? "rounded-2xl ring-1 ring-slate-200 overflow-hidden bg-white" : ""}
      style={{ width: "100%", height: fill ? "100%" : undefined }}
    >
      <iframe
        ref={frameRef}
        title={title}
        src={url}
        style={{ width: "100%", height: fill ? "100%" : `${minHeight}px`, border: 0, display: "block" }}
        loading="lazy"
        allow="clipboard-write"
        sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
      />
    </div>
  );
}
